import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  FileSignature,
  ClipboardList,
  Calendar,
  Users,
  Plus,
  Trash2,
  Folder,
  ChevronDown,
  LayoutTemplate,
  FilePlus,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import { useOrg } from "../../contexts/OrgContext.tsx";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Input from "../../components/common/form/input/InputField";
import { Card } from "../../components/common/Card";
import type { RequestType } from "../../types/request";
import {
  CreateRequestParticipantDto,
  FolderResponseDto,
  RequestTemplate,
  RequestType as RequestTypeEnum,
} from "@secure-caseworker/shared";
import {
  RequestTemplateEditor,
  FormTemplateData,
  TemplateValidationResult,
} from "../RequestTemplates/RequestTemplateEditor";
import formatOneOffTemplate from "./utils/formatOneOffTemplate.ts";

const DEFAULT_OUTPUT_FOLDER_NAME = "Completed Requests";

interface ParticipantInput {
  uiId: string;
  email: string;
  name: string;
}

export const CreateRequestPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const { setAlert } = useAlert();
  const { orgId, navigateOrg, navigateOrgUrl } = useOrg();

  const [requestMode, setRequestMode] = useState<"template" | "one_off">(
    "template",
  );

  const [oneOffTemplateData, setOneOffTemplateData] =
    useState<FormTemplateData | null>(null);
  const [oneOffValidation, setOneOffValidation] =
    useState<TemplateValidationResult>({ isValid: false });

  // State
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<RequestTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<RequestTemplate | null>(null);
  const [dueDate, setDueDate] = useState<string>("");
  const [participants, setParticipants] = useState<ParticipantInput[]>([]);

  // Output folder state
  const [folders, setFolders] = useState<FolderResponseDto[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [showFolderInput, setShowFolderInput] = useState(false);

  const currentTemplate = useMemo(() => {
    if (requestMode === "template") {
      return selectedTemplate
        ? {
            type: selectedTemplate.type,
            routing: selectedTemplate.routing,
            signers: selectedTemplate.signature_template_signers,
            title: selectedTemplate.title,
            description: selectedTemplate.description,
            fields: selectedTemplate.form_template_fields,
          }
        : null;
    }

    if (requestMode === "one_off") {
      return oneOffTemplateData
        ? {
            type: oneOffTemplateData.type,
            routing: oneOffTemplateData.routing,
            signers: oneOffTemplateData.signerRoles,
            title: oneOffTemplateData.title,
            description: oneOffTemplateData.description,
            fields: oneOffTemplateData.fields,
          }
        : null;
    }

    return null;
  }, [requestMode, selectedTemplate, oneOffTemplateData]);

  // Load templates and folders on mount
  useEffect(() => {
    if (orgId && caseId) {
      loadTemplates();
      loadFolders();
    }
  }, [orgId, caseId]);

  useEffect(() => {
    if (!currentTemplate) {
      setParticipants([]);
      return;
    }

    if (
      currentTemplate.type === "signature" &&
      currentTemplate.signers?.length
    ) {
      setParticipants(
        currentTemplate.signers.map((signer) => ({
          uiId: signer.id,
          email: "",
          name: "",
        })),
      );
      return;
    }

    setParticipants([
      {
        uiId: crypto.randomUUID(),
        email: "",
        name: "",
      },
    ]);
  }, [currentTemplate]);

  const loadTemplates = async () => {
    if (!orgId) return;

    setTemplatesLoading(true);
    try {
      const data = await apiClient.getRequestTemplates(orgId);
      setTemplates(data as RequestTemplate[]);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load templates",
      });
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadFolders = async () => {
    if (!caseId) return;

    setFoldersLoading(true);
    try {
      const data = await apiClient.getAllFolders(caseId);
      setFolders(data);

      // Find or set default folder "Completed Requests"
      const defaultFolder = data.find(
        (f) => f.name === DEFAULT_OUTPUT_FOLDER_NAME,
      );
      if (defaultFolder) {
        setSelectedFolderId(defaultFolder.id);
      }
    } catch (error) {
      console.error("Failed to load folders:", error);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleTemplateChange = async (templateId: string) => {
    setSelectedTemplateId(templateId);

    if (!templateId || !orgId) {
      setSelectedTemplate(null);
      setParticipants([]);
      return;
    }

    try {
      const template = await apiClient.getRequestTemplate(templateId, orgId);
      setSelectedTemplate(template as RequestTemplate);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load template details",
      });
    }
  };

  const handleParticipantChange = (
    participantId: string,
    field: "email" | "name",
    value: string,
  ) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.uiId === participantId ? { ...p, [field]: value } : p,
      ),
    );
  };

  const handleAddParticipant = () => {
    // Only for form type - add another responder
    const isFormType =
      requestMode === "template"
        ? selectedTemplate?.type === "form"
        : oneOffTemplateData?.type === "form";

    if (isFormType) {
      setParticipants((prev) => [
        ...prev,
        {
          uiId: crypto.randomUUID(),
          email: "",
          name: "",
        },
      ]);
    }
  };

  const mapParticipantsToDtos = (
    validParticipants: ParticipantInput[],
  ): CreateRequestParticipantDto[] => {
    return validParticipants.map((p) => ({
      email: p.email.trim(),
      name: p.name?.trim() || undefined,
    }));
  };

  const createRequest = async ({
    verifiedCaseId,
    validParticipants,
    outputFolderId,
    orgId,
    dueDate,
  }: {
    verifiedCaseId: string;
    validParticipants: ParticipantInput[];
    outputFolderId: string;
    orgId: string;
    dueDate?: string;
  }) => {
    const participantDtos = mapParticipantsToDtos(validParticipants);

    // Template mode
    if (requestMode === "template") {
      await apiClient.createRequest({
        template_id: selectedTemplateId,
        case_id: verifiedCaseId,
        due_date: dueDate || undefined,
        output_folder_id: outputFolderId,
        participants: participantDtos,
      });
      return;
    }

    // One-off mode
    await apiClient.createOneOffRequest({
      template: formatOneOffTemplate(orgId, oneOffTemplateData),
      request: {
        case_id: verifiedCaseId,
        output_folder_id: outputFolderId,
        participants: participantDtos,
        due_date: dueDate || undefined,
      },
    });
  };

  const resolveFolderId = async (
    verifiedCaseId: string,
    selectedFolderId?: string,
  ): Promise<string> => {
    if (selectedFolderId) {
      return Promise.resolve(selectedFolderId);
    }

    if (showFolderInput && newFolderName.trim()) {
      // Create a new folder
      const newFolder = await apiClient.createFolder({
        name: newFolderName.trim(),
        caseId: verifiedCaseId,
      });
      return newFolder.id;
    }

    // Create default "Completed Requests" folder if it doesn't exist
    const existingDefault = folders.find(
      (f) => f.name === DEFAULT_OUTPUT_FOLDER_NAME,
    );
    if (existingDefault) {
      return existingDefault.id;
    }
    const newFolder = await apiClient.createFolder({
      name: DEFAULT_OUTPUT_FOLDER_NAME,
      caseId: verifiedCaseId,
    });
    return newFolder.id;
  };

  const handleRemoveParticipant = (participantId: string) => {
    // Don't allow removing the last participant
    if (participants.length <= 1) return;

    setParticipants((prev) => prev.filter((p) => p.uiId !== participantId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!caseId || !orgId) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Missing case or organization information",
      });
      return;
    }

    // Template mode validation
    if (requestMode === "template" && !selectedTemplateId) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Please select a template",
      });
      return;
    }

    // One-off mode validation
    if (requestMode === "one_off" && !oneOffValidation.isValid) {
      setAlert({
        variant: "error",
        title: "Error",
        message: oneOffValidation.error || "Please complete the template form",
      });
      return;
    }

    // Validate participants
    const validParticipants = participants.filter((p) => p.email.trim());
    if (validParticipants.length === 0) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Please add at least one participant with an email address",
      });
      return;
    }

    // For signature templates/one-off, ensure all signer roles have participants
    const isSignature =
      requestMode === "template"
        ? selectedTemplate?.type === "signature"
        : oneOffTemplateData?.type === "signature";

    if (isSignature) {
      const missingEmails = participants.filter((p) => !p.email.trim());
      if (missingEmails.length > 0) {
        setAlert({
          variant: "error",
          title: "Error",
          message: "Please provide email addresses for all signers",
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Handle output folder - create new folder if needed
      const outputFolderId = await resolveFolderId(caseId, selectedFolderId);

      await createRequest({
        verifiedCaseId: caseId,
        validParticipants,
        outputFolderId,
        orgId,
      });

      setAlert({
        variant: "success",
        title: "Success",
        message: "Request created successfully. Invitations have been sent.",
      });

      navigateOrg(`/cases/${caseId}?tab=requests`);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to create request",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigateOrg(`/cases/${caseId}`);
  };

  const getTemplateIcon = (type: RequestType) => {
    return type === "signature" ? FileSignature : ClipboardList;
  };

  function getSignerLabel(signer: { name?: string; label?: string }) {
    return signer.label || signer.name || "Signer";
  }

  const shouldShowParticipants = (): boolean => {
    if (requestMode === "template") {
      // if request mode is existing template we shouldn't show participants until one is selected
      return !!selectedTemplate;
    }
    return !!oneOffTemplateData;
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: "Cases", path: navigateOrgUrl("/cases") },
          { label: "Case Details", path: navigateOrgUrl(`/cases/${caseId}`) },
          { label: "New Request" },
        ]}
      />

      <div className="flex items-center gap-3">
        <FileText size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Initiate New Request
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Send a form or signature request to participants
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Request Mode Toggle */}
          <div>
            <Label>Request Type</Label>
            <div className="mt-2 inline-flex rounded-lg border border-gray-200 p-1 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setRequestMode("template")}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  requestMode === "template"
                    ? "bg-brand-500 text-white"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                }`}
              >
                <LayoutTemplate size={16} />
                Use Template
              </button>
              <button
                type="button"
                onClick={() => setRequestMode("one_off")}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  requestMode === "one_off"
                    ? "bg-brand-500 text-white"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                }`}
              >
                <FilePlus size={16} />
                One-Off Request
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {requestMode === "template"
                ? "Send a request using an existing template"
                : "Create a custom request without saving it as a template"}
            </p>
          </div>

          {/* Template Selection (Template Mode) */}
          {requestMode === "template" && (
            <>
              <div>
                <Label>
                  Select Template <span className="text-error-500">*</span>
                </Label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={templatesLoading}
                  className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                >
                  <option value="">
                    {templatesLoading
                      ? "Loading templates..."
                      : "Choose a template"}
                  </option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      [{template.type === "signature" ? "Signature" : "Form"}]{" "}
                      {template.title}
                    </option>
                  ))}
                </select>
                {templates.length === 0 && !templatesLoading && (
                  <p className="mt-2 text-sm text-gray-500">
                    No templates available.{" "}
                    <button
                      type="button"
                      onClick={() => navigateOrg("/request-templates/new")}
                      className="text-brand-500 hover:underline"
                    >
                      Create one first
                    </button>
                  </p>
                )}
              </div>

              {/* Template Info */}
              {selectedTemplate && (
                <Card>
                  <div className="flex items-start gap-3">
                    {React.createElement(
                      getTemplateIcon(selectedTemplate.type),
                      {
                        size: 24,
                        className:
                          selectedTemplate.type === "signature"
                            ? "text-purple-500"
                            : "text-brand-500",
                      },
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-black dark:text-white">
                          {selectedTemplate.title}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            selectedTemplate.type === "signature"
                              ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                              : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          }`}
                        >
                          {selectedTemplate.type === "signature"
                            ? "Signature"
                            : "Form"}
                        </span>
                      </div>
                      {selectedTemplate.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {selectedTemplate.description}
                        </p>
                      )}
                      <div className="text-xs text-gray-500 mt-2">
                        {selectedTemplate.type === "form"
                          ? `${
                              selectedTemplate.form_template_fields?.length || 0
                            } fields`
                          : `${selectedTemplate.signer_count || 0} signers • ${
                              selectedTemplate.routing || "parallel"
                            } signing`}
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {/* One-Off Template Editor (One-Off Mode) */}
          {requestMode === "one_off" && (
            <div className="space-y-4">
              <div className="border-l-4 border-brand-500 pl-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Define Your Request
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Configure the form fields or signature documents for this
                  one-time request
                </p>
              </div>
              <RequestTemplateEditor
                embedded={true}
                onChange={(data, validation) => {
                  setOneOffTemplateData(data);
                  setOneOffValidation(validation);
                }}
                defaultType={RequestTypeEnum.FORM}
              />
            </div>
          )}

          {/* Due Date */}
          <div>
            <Label>Due Date (Optional)</Label>
            <div className="relative">
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
              <Calendar
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Output Folder */}
          <div>
            <Label>Output Folder</Label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Completed submissions will be saved to this folder
            </p>

            {showFolderInput ? (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Folder
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <Input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter new folder name"
                    className="pl-10"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowFolderInput(false);
                    setNewFolderName("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Folder
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10"
                  />
                  <select
                    value={selectedFolderId}
                    onChange={(e) => setSelectedFolderId(e.target.value)}
                    disabled={foldersLoading}
                    className="h-11 w-full rounded-lg border border-gray-300 bg-transparent pl-10 pr-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 appearance-none"
                  >
                    <option value="">
                      {foldersLoading
                        ? "Loading folders..."
                        : `${DEFAULT_OUTPUT_FOLDER_NAME} (Default)`}
                    </option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                        {folder.name === DEFAULT_OUTPUT_FOLDER_NAME
                          ? " (Default)"
                          : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFolderInput(true)}
                  startIcon={<Plus size={16} />}
                >
                  New Folder
                </Button>
              </div>
            )}
          </div>

          {/* Participants */}
          {shouldShowParticipants() && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>
                  {currentTemplate?.type === "signature"
                    ? "Signers"
                    : "Participants"}{" "}
                  <span className="text-error-500">*</span>
                </Label>
                {currentTemplate?.type === "form" && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleAddParticipant}
                    startIcon={<Plus size={16} />}
                  >
                    Add Participant
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {participants.map((participant, index) => (
                  <Card key={participant.uiId}>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <Users size={16} className="text-gray-500" />
                      </div>
                      <div className="flex-1 space-y-3">
                        {currentTemplate?.type === "signature" &&
                          currentTemplate.signers?.[index] && (
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {getSignerLabel(currentTemplate.signers[index])}
                              {currentTemplate.routing === "sequential" && (
                                <span className="ml-2 text-xs text-gray-500">
                                  (Signs #{index + 1})
                                </span>
                              )}
                            </div>
                          )}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Input
                              type="email"
                              value={participant.email}
                              onChange={(e) =>
                                handleParticipantChange(
                                  participant.uiId,
                                  "email",
                                  e.target.value,
                                )
                              }
                              placeholder="Email address *"
                              required
                            />
                          </div>
                          <div>
                            <Input
                              type="text"
                              value={participant.name}
                              onChange={(e) =>
                                handleParticipantChange(
                                  participant.uiId,
                                  "name",
                                  e.target.value,
                                )
                              }
                              placeholder="Name (optional)"
                            />
                          </div>
                        </div>
                      </div>
                      {/* Only show remove button for form type with multiple participants */}
                      {currentTemplate?.type === "form" &&
                        participants.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveParticipant(participant.uiId)
                            }
                            className="p-2 text-gray-400 hover:text-red-500"
                            title="Remove participant"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                loading ||
                (requestMode === "template" && !selectedTemplateId) ||
                (requestMode === "one_off" && !oneOffTemplateData?.title)
              }
            >
              {loading
                ? "Sending..."
                : requestMode === "template"
                  ? "Send Request"
                  : "Create & Send Request"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
